import useNavbarItems from 'Hooks/useNavbarItems';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { cn } from 'Utils/cn';
import PropTypes from 'prop-types';
import Switch from 'Components/Atoms/Switch/Switch';
import Collapse from 'Components/Atoms/Collapse/Collapse';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from 'Components/Atoms/Badge/Badge';

const itemVariants = {
    open: {
        opacity: 1,
        x: 0,
        transition: {
            ease: 'easeInOut',
            delay: 0.1,
        },
    },
    closed: {
        opacity: 0,
        x: 10,
        transition: {
            ease: 'easeInOut',
            delay: 0.1,
        },
    },
};

const dividerVariants = {
    open: {
        opacity: 0,
    },
    closed: {
        opacity: 1,
        transition: {
            delay: 0.1,
        },
    },
};

const dividerTextVariants = {
    open: {
        opacity: 1,
        y: 0,
        transition: {
            ease: 'easeInOut',
            delay: 0.1,
        },
    },
    closed: {
        opacity: 0,
        y: -5,
    },
};

const configMenuVariants = {
    open: {
        opacity: 1,
    },
    closed: {
        opacity: 0,
    },
};

export default function AppLayoutSidebar(props) {
    const { className, collapsed = false, editing = false } = props;
    const { navbarItems, configurationItems, onToogleItemHide } = useNavbarItems();

    const configMenuRef = useRef();
    const { t } = useTranslation('common');

    return (
        <nav className={cn('group', className)}>
            <motion.ul
                initial={false}
                className="flex flex-col gap-1.5"
                animate={collapsed ? 'closed' : 'open'}
                variants={{
                    open: {
                        width: '100%',
                        transition: {
                            type: 'spring',
                            duration: 0.4,
                            bounce: 0.2,
                        },
                    },
                    closed: {
                        width: 44,
                        transition: {
                            type: 'spring',
                            duration: 0.4,
                            bounce: 0.2,
                        },
                    },
                }}
            >
                {navbarItems
                    .filter((item) => !item.hidden || editing)
                    .map((item) =>
                        item.type === 'divider' ? (
                            <li key={item.id} className={cn('relative', item.className)}>
                                <motion.div
                                    key={item.id}
                                    variants={dividerVariants}
                                    className={cn(
                                        'h-px bg-gray-200 overflow-hidden absolute top-1/2 translate-y-[-50%] w-full left-0 pointer-events-none',
                                        item.className,
                                    )}
                                />
                                {item.label && (
                                    <motion.div
                                        variants={dividerTextVariants}
                                        className="text-gray-400 px-3 pt-3.5 pb-0.5 select-none uppercase font-semibold text-2xs tracking-widest"
                                    >
                                        {item.label}
                                    </motion.div>
                                )}
                            </li>
                        ) : (
                            <li
                                key={item.id}
                                className={cn(
                                    'hover:bg-gray-100 transition-colors flex items-center hover:text-dark-blue text-gray-500 rounded-md ',
                                    'group-hover:not-hover:bg-white',
                                    item.isCurrent && 'bg-gray-100 text-dark-blue',
                                    item.className,
                                )}
                            >
                                <Link
                                    className={clsx(
                                        'group/item grow',
                                        'flex no-underline py-2 px-3 text-current gap-3 text-sm items-center whitespace-nowrap',
                                    )}
                                    to={item.route}
                                    data-testid={`nav.${item.id}`}
                                    onClick={() => {
                                        configMenuRef.current?.close();
                                    }}
                                >
                                    <item.Icon size={20} color="currentColor" className="shrink-0" />

                                    <motion.div
                                        variants={itemVariants}
                                        className="flex items-center justify-between gap-2 grow"
                                    >
                                        <span>{item.label}</span>
                                        {!editing && item.badgeCount && (
                                            <Badge
                                                className={clsx(
                                                    'group-hover/item:bg-white transition-colors text-current group-hover:bg-gray-100 text-2xs leading-[18px]',
                                                    item.badgeVariant === 'danger' &&
                                                        'group-hover/item:bg-white group-hover:bg-danger/10 bg-danger/10 text-danger',
                                                    item.badgeVariant === 'warning' &&
                                                        'group-hover/item:bg-white group-hover:bg-warning/10 bg-warning/10 text-warning',
                                                    item.isCurrent && 'bg-white',
                                                )}
                                            >
                                                {item.badgeCount}
                                            </Badge>
                                        )}
                                    </motion.div>
                                </Link>
                                {editing && (
                                    <div className="px-2">
                                        <Switch
                                            size="small"
                                            className="relative after:absolute after:inset-[-10px] after:content-['']"
                                            checked={!item.hidden}
                                            onCheckedChange={() => onToogleItemHide(item.id)}
                                        />
                                    </div>
                                )}
                            </li>
                        ),
                    )}
            </motion.ul>

            <div className="h-3" />
            <motion.div
                className="mt-4 px-3 w-[calc(100%+theme(spacing.5))] -mx-2.5"
                initial={false}
                animate={collapsed ? 'closed' : 'open'}
                variants={configMenuVariants}
            >
                <Collapse
                    ref={configMenuRef}
                    classes={{
                        trigger:
                            'justify-between w-full px-3 py-2 text-2xs uppercase font-semibold text-gray-400 tracking-widest transition-colors group-hover/config:text-dark-blue',
                        content: 'pt-0',
                        icon: 'text-gray-400 group-hover/config:text-dark-blue',
                    }}
                    title={t('configuration')}
                    className="group/config bg-dark-blue/1 border border-dark-blue/5 rounded-lg"
                >
                    <ul className="flex flex-col items-start">
                        {configurationItems.map((item) => (
                            <li key={item.id}>
                                <Link
                                    className={clsx(
                                        'flex no-underline py-2 px-3 transition-colors gap-3 text-gray-500 hover:text-dark-blue text-xs items-center whitespace-nowrap',
                                    )}
                                    to={item.route}
                                    data-testid={`nav.${item.id}`}
                                    onClick={() => {
                                        configMenuRef.current?.close();
                                    }}
                                >
                                    <item.Icon size={18} color="currentColor" className="shrink-0" />

                                    <div className="flex items-center justify-between gap-2 grow">
                                        <span>{item.label}</span>
                                        {item.badgeCount && (
                                            <Badge
                                                className={clsx(
                                                    'text-2xs leading-[18px]',
                                                    item.badgeVariant === 'danger' && 'bg-danger/10 text-danger',
                                                    item.badgeVariant === 'warning' && 'bg-warning/10 text-warning',
                                                )}
                                            >
                                                {item.badgeCount}
                                            </Badge>
                                        )}
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </Collapse>
            </motion.div>
        </nav>
    );
}

AppLayoutSidebar.propTypes = {
    className: PropTypes.string,
    collapsed: PropTypes.bool,
    editing: PropTypes.bool,
};
